/** @jsx jsx */

import { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend as ChartJSLegend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { Grid, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { GRAPH_TIMESPANS } from './constants';
import { getGradient } from './utils';

import type { Statistics } from 'app/developer-v3/types/partnerDashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  ChartJSLegend
);

const Styles = {
  usageGraph: css`
    text-align: center;
  `,
  title: css`
    &&&&&& > span {
      text-transform: uppercase;
    }
  `,
};

type Props = {
  isBeta: boolean;
  statistics: Statistics;
};

function UsageGraph({ isBeta, statistics }: Props) {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const timespan = isBeta ? GRAPH_TIMESPANS.monthly : GRAPH_TIMESPANS.quarterly;

  // protects from unextendable array throwing an error inside chart.js
  const values = [].concat(statistics[timespan].values || []);
  const maxPoint = values.reduce((max, cur) => Math.max(max, cur), 0);
  const minPoint = values.reduce((min, cur) => Math.min(min, cur), 0);
  const ctx = chartRef.current?.canvas.getContext('2d');
  const colorGradient = ctx ? getGradient(timespan, ctx) : undefined;

  return isBeta && statistics[timespan] ? (
    <div css={Styles.usageGraph}>
      <Grid columns={1} gap={20}>
        <div css={Styles.title}>
          <Text type="SmallPrint4">A Look Back at Your Active Users</Text>
        </div>
      </Grid>
      <Line
        data={{
          datasets: [
            {
              backgroundColor: colorGradient,
              borderColor: colorGradient,
              data: values,
              fill: false,
              label: '',
              pointRadius: 4,
              pointStyle: 'rect',
              tension: 0,
            },
          ],
          labels: statistics[timespan].labels || [],
        }}
        options={{
          font: {
            family: `'Open Sans', Helvetica, arial, sans-serif`,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              mode: 'point',
              displayColors: false,
              callbacks: {
                title: () => '',
                label: field => field.label.toLocaleString(),
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              grid: {
                drawOnChartArea: false,
              },
            },
            y: {
              suggestedMin: Math.floor(minPoint * 0.8),
              suggestedMax: Math.ceil(maxPoint * 1.2 || 100),
              ticks: {
                precision: 0,
                callback: value => value.toLocaleString(),
              },
            },
          },
        }}
        ref={chartRef}
      />
    </div>
  ) : null;
}

export default UsageGraph;
